import { Component, OnInit, Input, EventEmitter, SimpleChanges, Output, OnChanges, OnDestroy } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import * as fromConfigurationStore from '@appStore/configuration';
import * as fromMeteringPointStore from '@appStore/metering-points';
import * as fromAccountStore from '@appStore/account';
import { UserInfo, Notification, MenuItem, MeteringPoint, BannerMessage } from '@model';
import { Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { environment } from '@environment';
import { TranslateService } from '@ngx-translate/core';
import { MenuItemType, HelpState, UserType, MeteringType } from '@enums';
import { SubSink } from 'subsink';
import { MeteringPointIntelligyOnboardStatus } from 'app/models/meteringpoint-intelligy-onboard-status.model';

@Component({
    selector: '[app-header]',
    templateUrl: './header.component.html',
    standalone: false
})
export class HeaderComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  isLoggedIn: boolean;
  @Input()
  isHandset: boolean;
  @Input()
  unreadNotificationCount: number;
  @Input()
  unreadMessagesCount: number;
  @Input()
  showFunctionsMenu: boolean;
  @Input()
  userInfo: UserInfo;
  @Input()
  userType: UserType;
  @Input()
  unreadNotifications$: Observable<Notification[]>
  @Input()
  bannerMessages: BannerMessage[]
  @Input()
  isEnableAppLauncher: boolean;
  @Input()
  meteringPoints: { meteringPoints: MeteringPoint[]; loaded: boolean };
  @Input()
  unreadTipsCount: number;

  itemTypeFunction: string;
  itemTypeLink: string;

  @Output()
  toggleEvent = new EventEmitter<boolean>();

  appRouter: Router;
  refreshUrl = this.sanitizer.bypassSecurityTrustResourceUrl('url');
  _unreadNotifications: Notification[];
  appLauncherMenuItems: MenuItem[] = [];
  activeAppLauncherItem: MenuItem;
  private subs = new SubSink();
  intelligyLoginUrl = '';
  UserTypeEnum = UserType;
  isIntelligyUser = false;

  isAppLauncherInitialized = false;
  

  
  constructor(
    private configurationStore: Store<fromConfigurationStore.State>,
    private meteringPointStore: Store<fromMeteringPointStore.State>,
    private accountStore: Store<fromAccountStore.State>,
    private router: Router,
    private sanitizer: DomSanitizer,
    private translate: TranslateService
  ) {
    this.appRouter = router;
  }

  get isAppLauncherVisible(){
    return this.isEnableAppLauncher && this.isAppLauncherInitialized 
    && (this.userType !== this.UserTypeEnum.Private);
    // && (this.userType !== this.UserTypeEnum.Private && this.userType !== this.UserTypeEnum.Manager);
  }

  get mobileTotalNotificationCount(){
    let totalCount = this.unreadMessagesCount + this.unreadNotificationCount;
    if (this.meteringPoints && this.meteringPoints.loaded){
      totalCount += this.meteringPoints.meteringPoints && this.meteringPoints.meteringPoints.length > 0 ? this.unreadTipsCount : 0;
    }

    return totalCount;
  }

  ngOnInit() {
    this.itemTypeFunction = MenuItemType.Function;
    this.itemTypeLink = MenuItemType.Link;

    if (this.isEnableAppLauncher) {
      this.setIntelligyStatus();
      this.initAppLauncher();
    }

    this.subs.sink = this.configurationStore
      .pipe(select(fromConfigurationStore.getApiConfiguration))
      .pipe(
        filter(x => !!x),
        take(1)
      )
      .subscribe(x => {
        this.intelligyLoginUrl = x.intelligyRedirectUrl;
      });  
  }

  ngOnChanges(changes: SimpleChanges) {
    for (const propName in changes) {
      if (changes.hasOwnProperty(propName)) {
        let change = changes[propName];
        switch (propName) {
          case 'meteringPoints': {
            this.setIntelligyStatus();
            this.initAppLauncher();
            break;
          }
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  setIntelligyStatus() {
   
    if (!this.isEnableAppLauncher) return;
    
    if (!this.meteringPoints) return;

    if (!this.meteringPoints.loaded) return;

    if (this.userType && this.userType === this.UserTypeEnum.Private) return;

    this.meteringPointStore.select(fromMeteringPointStore.getIsAnyMeteringPointUsingIntelligy)
      .pipe(
        take(1)
      ).subscribe(hasIntelligy => {
        this.isIntelligyUser = hasIntelligy;
      });
    this.isAppLauncherInitialized = true;
  }

  login() {
    this.router.navigate(['/login']);
  }

  contactMe() {
    window.location.href = `${environment.publicWebContactMeUrl}?name=${this.userInfo.name}&email=${
      this.userInfo.email
    }`;
  }

  toggleMenu() {
    this.toggleEvent.emit(true);
  }

  addCustomerRegistration = () => {
    this.router.navigate([`/secure/register/additional`]);
  };

  callbackFunction(callback: any) {
    if (!callback) {
      return;
    }
    
    callback();
  }

  initAppLauncher() {
    if (!this.isAppLauncherInitialized) return;

    this.appLauncherMenuItems = [];

    this.activeAppLauncherItem = {
      callbackFunction: () => {},
      displayText: this.translate.instant('core.header.applauncher.mina-sidor-title'),
      iconName: '',
      itemType: MenuItemType.Link,
      linkUrl: '/',
      id: "app-launcher-ms", 
      active: true
    } as MenuItem;
    
    this.appLauncherMenuItems.push(this.activeAppLauncherItem);

    if (this.isIntelligyUser) {
      this.appLauncherMenuItems.push({
        callbackFunction: () => { 
          this.accountStore.dispatch(new fromAccountStore.SetIntelligyLogin());
          window.open(this.intelligyLoginUrl, "_self");
        },
        displayText: this.translate.instant('core.header.applauncher.intelligy-title'),
        iconName: '',
        itemType: MenuItemType.Function,
        id: "app-launcher-in",
        disabled: !!this.userInfo.impersonatedAsName
      } as MenuItem);
    }
  }

  appLauncherFilter = () =>{
    let output = []
    if (this.appLauncherMenuItems){
      if (this.appLauncherMenuItems.length > 0 && this.activeAppLauncherItem){
        output = this.appLauncherMenuItems.filter(mi => mi.id !== this.activeAppLauncherItem.id)
      }
    }

    return output;
  }
}
